import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { get, action } from '@ember/object';
import { task } from 'ember-concurrency';
import { later } from '@ember/runloop';
import { resolve } from 'rsvp';
import { reads, equal } from 'macro-decorators';

import { CATEGORIES_LIST } from 'garaje/utils/integration-categories';

export default class IntegrationsIndexController extends Controller {
  @service state;
  @service featureFlags;
  @service integrationsManager;
  @service flashMessages;
  @service store;
  @service router;

  queryParams = ['scrollTo'];
  @tracked scrollTo = null; // null or a dom id, used to scroll to an element when the page loads

  @reads('state.currentLocation') currentLocation;
  @reads('state.currentCompany') currentCompany;
  @reads('currentCompany.saml.content') samlIntegration;
  @reads('currentCompany.googleApp.content') googleIntegration;
  @reads('currentCompany.oneLogin.content') oneLoginIntegration;
  @reads('currentCompany.oktaIntegration.content') oktaIntegration;
  @reads('currentCompany.hrisIntegration.content') hrisIntegration;
  @equal('currentCompany.scimIntegration.provider', 'onelogin') oneLoginSCIM;
  @equal('currentCompany.scimIntegration.provider', 'okta') oktaSCIM;
  @equal('currentCompany.scimIntegration.provider', 'azure') entraIdSCIM;
  @equal('currentCompany.scimIntegration.provider', 'centrify') delineaSCIM;
  @reads('currentCompany.scimIntegration.content') scimIntegration;
  @reads('state.features.canAccessDirectorySync') canAccessDirectorySync;
  @reads('state.features.canAccessSaml') canAccessSaml;

  // Master gate for the HRIS feature. See sibling controller in
  // pods/employees/integrations/controller.js for the full rationale.
  get hrisDirectorySyncEnabled() {
    return this.featureFlags.isEnabled('hris-directory-sync');
  }

  // Only `active` installs count — pending installs (wizard incomplete)
  // keep the tile in the "Install" state. See the mirror getter in
  // pods/employees/integrations/controller.js for the rationale.
  get hrisPluginInstall() {
    if (!this.hrisDirectorySyncEnabled) return undefined;
    return get(this.model, 'pluginInstalls')?.find((install) => {
      const status = get(install, 'status');
      const name = get(install, 'plugin.name') ?? '';
      return name.startsWith('HRIS') && status === 'active';
    });
  }

  // Pending HRIS plugin install — customer opened the wizard but didn't
  // finish the connection step. Drives the tile's "Configure" state.
  get pendingHrisPluginInstall() {
    if (!this.hrisDirectorySyncEnabled) return undefined;
    return get(this.model, 'pluginInstalls')?.find((install) => {
      const status = get(install, 'status');
      const name = get(install, 'plugin.name') ?? '';
      return name.startsWith('HRIS') && status === 'pending';
    });
  }

  get hasPendingHrisInstall() {
    return Boolean(this.pendingHrisPluginInstall);
  }

  get hrisPlugin() {
    const byName = (p) => (get(p, 'name') ?? '').startsWith('HRIS');
    return (
      get(this.model, 'plugins')?.find(byName) ||
      get(this.hrisPluginInstall, 'plugin') ||
      this.store.peekAll('plugin').find(byName)
    );
  }

  get hasHrisInstalled() {
    return Boolean(this.hrisPluginInstall);
  }

  // Truth is `company.employees_sync_provider` — envoy-web owns the mutex
  // + sync_mode/sync_state cascade on save. The old plugin_install.config
  // flag was dropped when app-store cut over to companies#update
  // (envoy-app-store#1573 / envoy-web EIP-3541).
  //
  // The `!hrisPluginInstall` guard suppresses the "HRIS owns the slot"
  // belief when the plugin install isn't loaded — otherwise a stale
  // `employees_sync_provider = 'hris'` in the DB (plugin uninstalled but
  // slot not cleared) would grey out every other provider tile.
  get hrisOwnsDirectorySync() {
    if (!this.hrisPluginInstall) return false;
    return get(this.currentCompany, 'employeesSyncProvider') === 'hris';
  }

  get hasScimIntegration() {
    return this.featureFlags.isEnabled('scim-integration', {
      created_at: get(this.currentCompany, 'createdAt'),
    });
  }

  get activeSyncPluginInstalls() {
    // find plugins in the sync category
    const syncPlugins = get(this.model, 'plugins').filterBy('category', 'sync');
    const pluginInstalls = get(this.model, 'pluginInstalls');

    // find active installs that belong to any plugin in the sync category
    const syncPluginInstalls = pluginInstalls.filter((install) => {
      return syncPlugins.findBy('id', get(install, 'plugin.id')) !== undefined;
    });

    return syncPluginInstalls.filter(function (install) {
      const status = get(install, 'status');
      return status === 'active' || status === 'pending';
    });
  }

  get currentSyncIntegration() {
    let currentIntegration = false;
    const activeSyncPluginInstalls = this.activeSyncPluginInstalls;

    if (this.googleIntegration) {
      currentIntegration = 'Google';
    }

    if (this.oneLoginIntegration || this.oneLoginSCIM) {
      currentIntegration = 'OneLogin';
    }

    if (this.oktaIntegration || this.oktaSCIM) {
      currentIntegration = 'Okta';
    }

    if (this.entraIdSCIM) {
      currentIntegration = 'Entra ID';
    }

    if (this.delineaSCIM) {
      currentIntegration = 'Delinea';
    }

    if (this.hrisOwnsDirectorySync) {
      currentIntegration = 'HRIS';
    }

    // This would say e.g. 'OKTA SCIM' if you installed the SCIM
    // version and then lost priviliges for SCIM.
    if (!this.featureFlags.isEnabled('scim-integration')) {
      if (this.scimIntegration) {
        currentIntegration += ' SCIM';
      }
    }

    if (activeSyncPluginInstalls.length) {
      currentIntegration = get(activeSyncPluginInstalls, 'firstObject.plugin.name');
    }

    return currentIntegration;
  }

  get hasSync() {
    return this.currentSyncIntegration !== false;
  }

  get categories() {
    return CATEGORIES_LIST.filter(
      ({ id, required }) => required || this.model.plugins.find(({ category }) => category === id),
    );
  }

  scrollToIntegration(selector) {
    later(function () {
      document.querySelector(selector).scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  }

  @task
  *installGoogleAppsTask() {
    const { currentCompany } = this.state;
    const integrationsManager = this.integrationsManager;
    // Clean any existing error this.store.unloadAll('google-app');
    this.store.unloadAll('google-app');

    yield integrationsManager
      .connectGoogleApps(currentCompany)
      .then(() => {
        currentCompany.reload();
        this.router.transitionTo('employees.sync-settings');
      })
      .catch((response) => {
        let message = "Sorry, we couldn't connect your account.";
        const [error] = response.errors;

        if (error) {
          message = error.messages.join(', ');
        }

        this.flashMessages.showAndHideFlash('error', message);
      });
  }

  @action
  goToPluginInstallConfig(integration) {
    this.router.transitionTo('integrations.enabled', { queryParams: { integration } });
  }

  @task
  *installOneLoginTask() {
    const { currentCompany } = this.state;
    this.store.createRecord('one-login', { company: currentCompany });
    yield this.router.transitionTo('integrations.enabled', { queryParams: { integration: 'one-login' } }).then(() => {
      this.scrollToIntegration('#one-login-config');
    });
  }

  @task
  *installOktaTask() {
    const { currentCompany } = this.state;
    this.store.createRecord('okta-integration', { company: currentCompany });
    yield this.router.transitionTo('integrations.enabled', { queryParams: { integration: 'okta' } }).then(() => {
      this.scrollToIntegration('#okta-config');
    });
  }

  /**
   * HRIS connect/manage lives in the app-store plugin (slug: `hris`), not as
   * an inline config-box on this page. Both the "Install" and "Manage" CTAs
   * deep-link to the plugin via the dynamic apps route.
   */
  // "Install" CTA — see employees/integrations/controller.js for the full
  // four-state explanation.
  @task
  *installHrisTask() {
    const pluginKey = get(this.hrisPluginInstall, 'plugin.key') || get(this.hrisPlugin, 'key');
    if (pluginKey) {
      yield this.router.transitionTo('apps.dynamic', `by/${pluginKey}`);
      return;
    }
    yield this.router.transitionTo('apps.dynamic', 'integrations');
  }

  // "Enable" / "Disable" CTAs — manage the directory-sync slot on the
  // dedicated HRIS sync settings page.
  @task
  *goToHrisSettingsTask() {
    yield this.router.transitionTo('employees.hris-settings');
  }

  @task
  *installScimTask(provider) {
    const { currentCompany } = this.state;
    this.store.createRecord('scim-integration', { company: currentCompany, provider });

    yield this.router.transitionTo('integrations.enabled', { queryParams: { integration: 'scim' } }).then(() => {
      this.scrollToIntegration('#scim-config');
    });
  }

  @task
  *installSamlTask() {
    const { currentCompany } = this.state;
    this.store.createRecord('saml', { company: currentCompany });
    yield this.router.transitionTo('integrations.enabled', { queryParams: { integration: 'saml' } }).then(() => {
      this.scrollToIntegration('.integrations-saml');
    });
  }

  @task
  *installWebhookTask() {
    const { currentLocation } = this.state;
    this.store.createRecord('webhook', { location: currentLocation });
    yield this.router.transitionTo('integrations.enabled', { queryParams: { integration: 'webhook' } }).then(() => {
      this.scrollToIntegration('#webhook-config');
    });
  }

  @task
  *installZapierTask() {
    const { currentCompany } = this.state;
    const integrationsManager = this.integrationsManager;
    return yield integrationsManager.connectZapier(currentCompany).catch(() => {
      let promise;

      // Don't reload zapier if is already in memory.
      if (get(currentCompany, 'zapierIntegration.content')) {
        promise = resolve({});
      } else {
        promise = integrationsManager.loadZapier();
      }

      return promise.then(() => {
        this.router.transitionTo('integrations.enabled', {
          queryParams: {
            integration: 'zapier',
          },
        });
      });
    });
  }

  @task
  *installIftttTask() {
    const { currentCompany } = this.state;
    const integrationsManager = this.integrationsManager;
    return yield integrationsManager.connectIfttt(currentCompany).catch(() => {
      let promise;

      if (get(currentCompany, 'iftttIntegration.content')) {
        promise = resolve({});
      } else {
        promise = integrationsManager.loadIfttt();
      }

      return promise.then(() => {
        this.router.transitionTo('integrations.enabled', { queryParams: { integration: 'ifttt' } });
      });
    });
  }
}
